/**
 * FrietchaletMenu — Namespace voor alle menu pagina functionaliteit
 *
 * Adaptatie van reference design (docs/refrences/product-collection-page-ideas/app.js)
 * naar Shopify native integratie met Cart AJAX API.
 *
 * Shopify Cart AJAX API docs:
 * - GET  /cart.js          — haal cart op
 * - POST /cart/add.js      — voeg item toe
 * - POST /cart/change.js   — wijzig hoeveelheid (id = line item key)
 */
const FrietchaletMenu = (function() {
  'use strict';

  // ─── CONFIG ──────────────────────────────────────────────
  let config = {};
  let infoData = {};

  // ─── STATE ───────────────────────────────────────────────
  let activeTab = '';
  let scrollSpyObserver = null;
  let scrollSpyPaused = false;
  let searchQuery = '';
  let modalProductData = null;
  let modalQty = 1;
  let modalSelectedVariantId = null;
  let modalSelectedOptions = [];
  let modalDescription = '';
  let searchDebounce = null;
  let mobileCartOpen = false;
  let activeInfoTab = 'reviews';
  let lastDeletedItem = null;  // { variantId, prevQuantity, title } — voor undo
  let undoTimeout = null;

  // ─── INIT ────────────────────────────────────────────────
  function init() {
    // Parse config van Liquid-gerenderde JSON
    const configEl = document.getElementById('frietchalet-shopify-config');
    if (configEl) {
      try { config = JSON.parse(configEl.textContent); } catch(e) { console.error('FrietchaletMenu: config parse error', e); }
    }

    const infoEl = document.getElementById('frietchalet-info-data');
    if (infoEl) {
      try { infoData = JSON.parse(infoEl.textContent); } catch(e) { console.error('FrietchaletMenu: info data parse error', e); }
    }

    // Set --fm-header-height op basis van Shopify theme header
    setHeaderHeight();

    // Uitgestelde metingen: voor late font/image loads die header hoogte beïnvloeden
    requestAnimationFrame(setHeaderHeight);
    setTimeout(setHeaderHeight, 100);
    setTimeout(setHeaderHeight, 500);

    // MutationObserver (header-hidden class) + ResizeObserver (grootte)
    initHeaderTracking();

    // Bij resize: reset caches, herbereken alles
    window.addEventListener('resize', function() {
      clearTimeout(_headerHideTimer);
      _headerEl = null;
      if (_headerRO) { _headerRO.disconnect(); _headerRO = null; }
      if (_headerMO) { _headerMO.disconnect(); _headerMO = null; }
      setHeaderHeight();
      initHeaderTracking();
      initScrollSpy();
    });

    // Eerste tab activeren
    const firstTab = document.querySelector('#frietchalet-tabs-container .tab-btn');
    if (firstTab) activeTab = firstTab.dataset.tabHandle;

    // Scroll spy initialiseren
    initScrollSpy();

    // CTA vanaf de homepage: juiste product tonen
    handleDeepLink();

    // Scroll shadow affordances voor sidebar scroll zones
    initScrollShadows();

    // Bij overgang naar desktop: sluit mobile cart, move Dingdoong terug
    var desktopMq = window.matchMedia('(min-width: 1024px)');
    desktopMq.addEventListener('change', function(e) {
      if (e.matches && mobileCartOpen) {
        closeMobileCart();
      }
    });

    // Cart laden bij pagina init
    fetchCart();

    if (!window.__frietchaletMenuCartRefreshBound) {
      window.__frietchaletMenuCartRefreshBound = true;
      document.addEventListener('cart:refresh', function () {
        if (_selfCartDispatch) return;
        fetchCart();
      });
    }
  }

  // ─── HEADER HEIGHT ───────────────────────────────────────
  //
  // Landing-header.liquid behavior:
  //   - <header class="landing-header-sticky" data-section-type="landing-header">
  //   - Scroll > 100px  → switches to position: fixed (class: using-fixed)
  //   - Scroll DOWN     → class header-hidden added  → transform: translateY(-100%)
  //   - Scroll UP       → class header-hidden removed → transform: translateY(0)
  //   - transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)
  //
  // Strategy:
  //   - VISIBLE  → --fm-header-height = header.offsetHeight (immediate)
  //   - HIDDEN   → --fm-header-height = 0px (after 320ms delay, post-animation)

  var _headerEl        = null;
  var _headerMO        = null; // MutationObserver for class changes
  var _headerRO        = null; // ResizeObserver for size changes
  var _scrollSpyReinit = null;
  var _headerHideTimer = null;

  function getHeaderEl() {
    if (!_headerEl) {
      // landing-header.liquid: <header data-section-type="landing-header" class="landing-header-sticky ...">
      _headerEl = document.querySelector('[data-section-type="landing-header"]') ||
                  document.querySelector('.landing-header-sticky') ||
                  // Fallback: standard Canopy header / generic header
                  document.querySelector('.section-header') ||
                  document.querySelector('header[role="banner"]');
    }
    return _headerEl;
  }

  function isHeaderHidden() {
    var h = getHeaderEl();
    if (!h) return false;
    // landing-header.liquid uses 'header-hidden' class
    return h.classList.contains('header-hidden');
  }

  function setHeaderHeight() {
    clearTimeout(_headerHideTimer);

    var h = getHeaderEl();

    // If header is NOT sticky/fixed (disabled via CSS on this page), always 0.
    // Our category tabs are the primary sticky element — like the reference design.
    if (h) {
      var pos = window.getComputedStyle(h).position;
      if (pos !== 'sticky' && pos !== 'fixed' && pos !== '-webkit-sticky') {
        document.documentElement.style.setProperty('--fm-header-height', '0px');
        return;
      }
    }

    if (isHeaderHidden()) {
      // Header is sliding away (transform: translateY(-100%), 0.3s transition).
      // Wait for animation to finish BEFORE moving tabs to top: 0.
      _headerHideTimer = setTimeout(function() {
        if (isHeaderHidden()) {
          document.documentElement.style.setProperty('--fm-header-height', '0px');
          clearTimeout(_scrollSpyReinit);
          _scrollSpyReinit = setTimeout(initScrollSpy, 50);
        }
      }, 320);
    } else {
      // Header is VISIBLE and sticky/fixed — set height so tabs are below header.
      var height = h ? h.offsetHeight : 0;
      document.documentElement.style.setProperty('--fm-header-height', height + 'px');
      clearTimeout(_scrollSpyReinit);
      _scrollSpyReinit = setTimeout(initScrollSpy, 350);
    }
  }

  function initHeaderTracking() {
    var h = getHeaderEl();
    if (!h) return;

    // ResizeObserver: update when header size changes (font load, responsive)
    if (window.ResizeObserver) {
      if (_headerRO) _headerRO.disconnect();
      _headerRO = new ResizeObserver(setHeaderHeight);
      _headerRO.observe(h);
    }

    // MutationObserver: detect header-hidden / using-fixed class changes
    if (_headerMO) _headerMO.disconnect();
    _headerMO = new MutationObserver(setHeaderHeight);
    _headerMO.observe(h, { attributes: true, attributeFilter: ['class'] });
  }

  // ─── SHOPIFY PRICE FORMATTER ─────────────────────────────
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseInt(cents, 10);
    if (isNaN(value)) return '€0,00';
    const amount = (value / 100).toFixed(2).replace('.', ',');
    if (config.money_format) {
      return config.money_format
        .replace('{{amount}}', amount)
        .replace('{{ amount }}', amount)
        .replace('{{amount_with_comma_separator}}', amount)
        .replace('{{ amount_with_comma_separator }}', amount);
    }
    return '\u20ac' + amount;
  }

  // ─── CART API ────────────────────────────────────────────

  var _selfCartDispatch = false;

  function fetchCart() {
    return fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        renderAllCarts(cart);
        return cart;
      })
      .catch(function(e) { console.error('FrietchaletMenu: fetchCart error', e); });
  }

  function addToCart(variantId, quantity) {
    // Winkel vandaag gesloten → niets toevoegen aan de winkelwagen
    if (isShopClosed()) { openClosedModal(); return Promise.resolve(null); }
    var url = (config.cart_add_url) || '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: quantity || 1 })
    })
    .then(function(r) {
      if (!r.ok) { return r.json().then(function(err) { throw err; }); }
      return r.json();
    })
    .then(function() { return fetchCart(); })
    .then(function(cart) {
      triggerCartBadgePop();
      document.dispatchEvent(new CustomEvent('cart:item-added'));
      return cart;
    })
    .catch(function(e) {
      console.error('FrietchaletMenu: addToCart error', e);
      var msg = (e && e.description) ? e.description : 'Er is iets misgegaan. Probeer opnieuw.';
      alert(msg);
    });
  }

  function changeCartItem(lineItemKey, quantity) {
    // Capture deleted item data before API call (for undo)
    if (parseInt(quantity, 10) === 0) {
      var row = document.querySelector('[data-item-key="' + lineItemKey + '"]');
      if (row) {
        var titleEl = row.querySelector('.cart-item-name-new');
        lastDeletedItem = {
          variantId: row.dataset.variantId,
          prevQuantity: parseInt(row.dataset.itemQty, 10) || 1,
          title: titleEl ? titleEl.textContent : 'Item'
        };
      }
    } else {
      // Quantity change (not delete) — clear any pending undo
      hideUndoToast();
    }

    var url = (config.cart_change_url) || '/cart/change.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity: parseInt(quantity, 10) })
    })
    .then(function(r) {
      if (!r.ok) { return r.json().then(function(err) { throw err; }); }
      return r.json();
    })
    .then(function(cart) {
      renderAllCarts(cart);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      // Show undo toast after successful delete
      if (parseInt(quantity, 10) === 0 && lastDeletedItem) {
        showUndoToast(lastDeletedItem.title);
      }
      return cart;
    })
    .catch(function(e) { console.error('FrietchaletMenu: changeCartItem error', e); });
  }

  function showUndoToast(title) {
    clearTimeout(undoTimeout);
    var label = escapeHtml(title.length > 28 ? title.substring(0, 28) + '…' : title);
    var html = '<span>' + label + ' verwijderd</span>' +
               '<button class="cart-undo-btn" onclick="FrietchaletMenu.undoLastDelete()">Ongedaan maken</button>';

    ['frietchalet-mobile-undo-toast', 'frietchalet-desktop-undo-toast'].forEach(function(id) {
      var toast = document.getElementById(id);
      if (!toast) return;
      toast.innerHTML = html;
      toast.classList.remove('hidden');
    });

    undoTimeout = setTimeout(function() {
      hideUndoToast();
      lastDeletedItem = null;
    }, 3000);
  }

  function hideUndoToast() {
    clearTimeout(undoTimeout);
    ['frietchalet-mobile-undo-toast', 'frietchalet-desktop-undo-toast'].forEach(function(id) {
      var toast = document.getElementById(id);
      if (toast) toast.classList.add('hidden');
    });
  }

  function undoLastDelete() {
    if (!lastDeletedItem) return;
    var item = lastDeletedItem;
    lastDeletedItem = null;
    hideUndoToast();

    var url = (config.cart_add_url) || '/cart/add.js';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.variantId, quantity: item.prevQuantity })
    })
    .then(function(r) {
      if (!r.ok) { return r.json().then(function(err) { throw err; }); }
      return r.json();
    })
    .then(function() {
      // Refetch full cart to update all UI
      return fetch('/cart.js').then(function(r) { return r.json(); });
    })
    .then(function(cart) {
      renderAllCarts(cart);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    })
    .catch(function(e) { console.error('FrietchaletMenu: undoLastDelete error', e); });
  }

  // ─── SCROLL SHADOW AFFORDANCES ────────────────────────────

  function updateScrollShadows(el) {
    if (!el) return;
    var hasOverflow = el.scrollHeight > el.clientHeight + 1;
    el.classList.toggle('has-overflow', hasOverflow);
    if (!hasOverflow) {
      el.classList.remove('at-top', 'at-bottom');
      return;
    }
    var atTop = el.scrollTop < 2;
    var atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    el.classList.toggle('at-top', atTop);
    el.classList.toggle('at-bottom', atBottom);
  }

  function initScrollShadows() {
    var cartItems = document.getElementById('frietchalet-cart-items');
    if (!cartItems) return;

    var ticking = false;
    cartItems.addEventListener('scroll', function() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function() {
          updateScrollShadows(cartItems);
          ticking = false;
        });
      }
    }, { passive: true });
    updateScrollShadows(cartItems);
  }

  // ─── CART RENDERING ──────────────────────────────────────

  function renderAllCarts(cart) {
    renderDesktopCart(cart);
    renderMobileCart(cart);
    renderMobileFloatBtn(cart);
    notifyStoreCart(cart);
  }

  // PER-STORE INTEGRATION (Frietchalet) — keep the theme's header/drawer cart count in sync
  function notifyStoreCart(cart) {
    try {
      var jq = window.jQuery || window.$;
      if (jq) { jq(window).trigger('cc-cart-updated', { addedToCart: true }); }
      if (window.theme && typeof window.theme.updateCartSummaries === 'function') {
        window.theme.updateCartSummaries(false, false, false);
      }
      _selfCartDispatch = true;
      try {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
      } finally {
        _selfCartDispatch = false;
      }
    } catch (e) {}
  }

  function renderCartItemRow(item) {
    var existingNote = (item.properties && item.properties['Notitie']) ? item.properties['Notitie'] : '';

    return '<div class="cart-item-row" data-item-key="' + escapeHtml(item.key) + '" data-item-qty="' + item.quantity + '" data-variant-id="' + escapeHtml(String(item.variant_id)) + '">' +
      '<div class="cart-item-top">' +
        '<span class="cart-item-name-new">' + escapeHtml(item.title) + '</span>' +
        '<span class="cart-item-price-new">' + formatMoney(item.final_line_price) + '</span>' +
      '</div>' +
      (item.variant_title ? '<div class="cart-item-opts">' + escapeHtml(item.variant_title) + '</div>' : '') +
      (existingNote ? '<div class="cart-item-note-display">' + escapeHtml(existingNote) + '</div>' : '') +
      '<div class="cart-item-note-wrap hidden">' +
        '<textarea class="cart-item-note-input" placeholder="Bijv. zonder zout, extra saus..." rows="2" maxlength="200">' + escapeHtml(existingNote) + '</textarea>' +
        '<div class="cart-item-note-actions">' +
          '<button class="cart-item-note-cancel" onclick="FrietchaletMenu.closeCartItemNote(this)">Annuleer</button>' +
          '<button class="cart-item-note-save" onclick="FrietchaletMenu.saveCartItemNote(this)">Opslaan</button>' +
        '</div>' +
      '</div>' +
      '<div class="cart-item-bottom">' +
        '<button class="cart-add-note-btn" onclick="FrietchaletMenu.openCartItemNote(this)">' +
          (existingNote ? 'Notitie wijzigen' : 'Notitie toevoegen') +
        '</button>' +
        '<div class="cart-qty-row">' +
          '<button class="cart-qty-btn" onclick="FrietchaletMenu.changeCartItem(\'' + item.key + '\', 0)" aria-label="Verwijder">' +
            '<svg class="fi" style="font-size:14px;line-height:1" aria-hidden="true"><use href="#fi-rr-trash"/></svg>' +
          '</button>' +
          '<span class="cart-qty-num">' + item.quantity + '</span>' +
          '<button class="cart-qty-btn" onclick="FrietchaletMenu.changeCartItem(\'' + item.key + '\', ' + (item.quantity + 1) + ')" aria-label="Meer">' +
            '<svg class="fi" style="font-size:14px;line-height:1" aria-hidden="true"><use href="#fi-rr-plus"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function openCartItemNote(btn) {
    var row = btn.closest('.cart-item-row');
    if (!row) return;
    var wrap = row.querySelector('.cart-item-note-wrap');
    if (!wrap) return;
    wrap.classList.remove('hidden');
    var textarea = wrap.querySelector('.cart-item-note-input');
    if (textarea) {
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  }

  function closeCartItemNote(btn) {
    var row = btn.closest('.cart-item-row');
    if (!row) return;
    var wrap = row.querySelector('.cart-item-note-wrap');
    if (wrap) wrap.classList.add('hidden');
  }

  function saveCartItemNote(btn) {
    var row = btn.closest('.cart-item-row');
    if (!row) return;
    var key = row.dataset.itemKey;
    var quantity = parseInt(row.dataset.itemQty, 10);
    var wrap = row.querySelector('.cart-item-note-wrap');
    var textarea = wrap ? wrap.querySelector('.cart-item-note-input') : null;
    var note = textarea ? textarea.value.trim() : '';

    btn.disabled = true;
    btn.textContent = '...';

    var properties = {};
    if (note) properties['Notitie'] = note;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, properties: properties })
    })
    .then(function(r) {
      if (!r.ok) return r.json().then(function(err) { throw err; });
      return r.json();
    })
    .then(function(cart) {
      renderAllCarts(cart);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    })
    .catch(function(e) {
      console.error('FrietchaletMenu: saveCartItemNote error', e);
      btn.disabled = false;
      btn.textContent = 'Opslaan';
    });
  }

  function renderCartSummary(cart) {
    return '<div class="cart-summary-new">' +
      '<div class="cart-summary-row">' +
        '<span>Subtotaal</span>' +
        '<span>' + formatMoney(cart.total_price) + '</span>' +
      '</div>' +
      '<div class="cart-total-new">' +
        '<span>Totaal</span>' +
        '<span>' + formatMoney(cart.total_price) + '</span>' +
      '</div>' +
    '</div>';
  }

  function renderCartEmptyState() {
    return '<div class="cart-empty">' +
      '<div class="cart-empty-icon">' +
        '<svg class="fi" style="font-size:40px;line-height:1" aria-hidden="true"><use href="#fi-rr-bags-shopping"/></svg>' +
      '</div>' +
      '<h3 class="cart-empty-title">Vul je winkelwagen</h3>' +
      '<p class="cart-empty-subtitle">Voeg iets van het menu toe om te beginnen</p>' +
    '</div>';
  }

  function renderDesktopCart(cart) {
    var itemsEl = document.getElementById('frietchalet-cart-items');
    var summaryEl = document.getElementById('frietchalet-cart-summary');
    var checkoutEl = document.getElementById('frietchalet-cart-checkout');
    var checkoutBtn = document.getElementById('frietchalet-checkout-btn');
    var sectionHeader = document.getElementById('frietchalet-cart-section-header');
    if (!itemsEl) return;

    if (!cart || cart.item_count === 0) {
      itemsEl.innerHTML = renderCartEmptyState();
      if (summaryEl) summaryEl.classList.add('hidden');
      if (checkoutEl) checkoutEl.classList.remove('hidden');
      if (checkoutBtn) { checkoutBtn.disabled = true; checkoutBtn.textContent = 'Afrekenen'; }
      if (sectionHeader) sectionHeader.classList.add('hidden');
    } else {
      var itemsHTML = cart.items.map(function(item) { return renderCartItemRow(item); }).join('');
      itemsEl.innerHTML = '<div class="cart-items-list">' + itemsHTML + '</div>';
      if (summaryEl) {
        summaryEl.classList.remove('hidden');
        summaryEl.innerHTML = renderCartSummary(cart);
      }
      if (checkoutEl) checkoutEl.classList.remove('hidden');
      if (checkoutBtn) { checkoutBtn.disabled = false; checkoutBtn.textContent = 'Afrekenen (' + formatMoney(cart.total_price) + ')'; }
      if (sectionHeader) {
        sectionHeader.textContent = 'Mijn items (' + cart.item_count + ')';
        sectionHeader.classList.remove('hidden');
      }
    }
    requestAnimationFrame(function() { updateScrollShadows(itemsEl); });
  }

  function renderMobileCart(cart) {
    var el = document.getElementById('frietchalet-mobile-cart-content');
    var summaryEl = document.getElementById('frietchalet-mobile-cart-summary');
    var checkoutEl = document.getElementById('frietchalet-mobile-cart-checkout');
    var checkoutBtn = document.getElementById('frietchalet-mobile-checkout-btn');
    if (!el) return;
    if (!cart || cart.item_count === 0) {
      el.innerHTML = renderCartEmptyState();
      if (summaryEl) { summaryEl.classList.add('hidden'); summaryEl.innerHTML = ''; }
      if (checkoutEl) checkoutEl.classList.remove('hidden');
      if (checkoutBtn) { checkoutBtn.disabled = true; checkoutBtn.textContent = 'Afrekenen'; }
    } else {
      var itemsHTML = cart.items.map(function(item) { return renderCartItemRow(item); }).join('');
      el.innerHTML = '<div class="cart-items-list">' + itemsHTML + '</div>';
      if (summaryEl) { summaryEl.classList.remove('hidden'); summaryEl.innerHTML = renderCartSummary(cart); }
      if (checkoutEl) checkoutEl.classList.remove('hidden');
      if (checkoutBtn) { checkoutBtn.disabled = false; checkoutBtn.textContent = 'Afrekenen (' + formatMoney(cart.total_price) + ')'; }
    }
  }

  function renderMobileFloatBtn(cart) {
    var btn = document.getElementById('frietchalet-mobile-cart-btn');
    if (!btn) return;
    if (!cart || cart.item_count === 0) {
      btn.classList.add('hidden');
      return;
    }
    btn.classList.remove('hidden');
    var countEl = document.getElementById('frietchalet-float-count');
    var totalEl = document.getElementById('frietchalet-float-total');
    if (countEl) countEl.textContent = cart.item_count;
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
  }

  // ─── SCROLL SPY ──────────────────────────────────────────

  function getStickyOffset() {
    var headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--fm-header-height'), 10) || 0;
    var tabsEl = document.getElementById('frietchalet-category-tabs');
    var tabsHeight = tabsEl ? tabsEl.offsetHeight : 54;
    return headerHeight + tabsHeight;
  }

  function initScrollSpy() {
    if (scrollSpyObserver) scrollSpyObserver.disconnect();
    var sections = document.querySelectorAll('.frietchalet-menu .menu-category-section');
    if (!sections.length) return;

    var stickyOffset = getStickyOffset();
    var topMargin = '-' + (stickyOffset + 8) + 'px';

    scrollSpyObserver = new IntersectionObserver(function(entries) {
      if (scrollSpyPaused) return;
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var handle = entry.target.id.replace('section-', '');
          setActiveTab(handle);
        }
      });
    }, {
      rootMargin: topMargin + ' 0px -55% 0px',
      threshold: 0
    });

    sections.forEach(function(s) { scrollSpyObserver.observe(s); });
  }

  function setActiveTab(handle) {
    activeTab = handle;
    var container = document.getElementById('frietchalet-tabs-container');
    if (!container) return;
    container.querySelectorAll('.tab-btn').forEach(function(btn) {
      var isActive = btn.dataset.tabHandle === handle;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });
    scrollActiveTabIntoView();
  }

  function scrollActiveTabIntoView() {
    var container = document.getElementById('frietchalet-tabs-container');
    if (!container) return;
    var activeBtn = container.querySelector('.tab-btn.active');
    if (!activeBtn) return;
    var cRect = container.getBoundingClientRect();
    var bRect = activeBtn.getBoundingClientRect();
    var scrollLeft = container.scrollLeft + (bRect.left - cRect.left) - (cRect.width / 2) + (bRect.width / 2);
    container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }

  function scrollToCategory(handle) {
    var section = document.getElementById('section-' + handle);
    if (!section) return;
    var offset = getStickyOffset() + 8;
    var top = section.getBoundingClientRect().top + window.scrollY - offset;

    scrollSpyPaused = true;
    window.scrollTo({ top: top, behavior: 'smooth' });
    setActiveTab(handle);
    setTimeout(function() { scrollSpyPaused = false; }, 800);
  }

  function scrollTabs(direction) {
    var container = document.getElementById('frietchalet-tabs-container');
    if (!container) return;
    container.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  }

  // ─── MENU SEARCH ─────────────────────────────────────────

  // "sate" moet ook "saté" vinden, en "creme" ook "crème".
  function foldAccents(s) {
    s = String(s).toLowerCase();
    return s.normalize ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : s;
  }

  function onMenuSearch(value) {
    searchQuery = foldAccents(value.trim());
    var clearBtn = document.getElementById('frietchalet-search-clear');
    if (clearBtn) clearBtn.classList.toggle('hidden', searchQuery === '');
    // Alle producten opnieuw doorlopen bij elke toetsaanslag is te duur op mobiel.
    if (searchDebounce) clearTimeout(searchDebounce);
    searchDebounce = setTimeout(applyMenuSearch, 120);
  }

  function clearMenuSearch() {
    searchQuery = '';
    if (searchDebounce) { clearTimeout(searchDebounce); searchDebounce = null; }
    var input = document.getElementById('frietchalet-menu-search');
    if (input) input.value = '';
    var clearBtn = document.getElementById('frietchalet-search-clear');
    if (clearBtn) clearBtn.classList.add('hidden');
    applyMenuSearch();
  }

  function applyMenuSearch() {
    var sections = document.querySelectorAll('.frietchalet-menu .menu-category-section');
    var totalVisible = 0;

    sections.forEach(function(section) {
      if (!searchQuery) {
        section.style.display = '';
        section.querySelectorAll('.menu-item').forEach(function(el) { el.style.display = ''; });
        return;
      }
      var items = section.querySelectorAll('.menu-item');
      var visibleInSection = 0;

      items.forEach(function(item) {
        if (item._fmHaystack === undefined) {
          item._fmHaystack = foldAccents((item.dataset.productTitle || '') + ' ' + (item.dataset.productDescription || ''));
        }
        var matches = item._fmHaystack.indexOf(searchQuery) !== -1;
        item.style.display = matches ? '' : 'none';
        if (matches) visibleInSection++;
      });

      section.style.display = visibleInSection > 0 ? '' : 'none';
      totalVisible += visibleInSection;
    });

    var noResults = document.getElementById('frietchalet-no-results');
    if (noResults) {
      noResults.classList.toggle('visible', searchQuery !== '' && totalVisible === 0);
    }

    // Tabs blijven staan tijdens het zoeken; alleen tabs zonder treffer verdwijnen.
    var tabsContainer = document.getElementById('frietchalet-tabs-container');
    if (tabsContainer) {
      tabsContainer.querySelectorAll('.tab-btn').forEach(function(btn) {
        var handle = btn.dataset.tabHandle;
        var sec = handle ? document.getElementById('section-' + handle) : null;
        btn.style.display = (sec && sec.style.display === 'none') ? 'none' : '';
      });
    }
  }

  // ─── PRODUCT MODAL ───────────────────────────────────────

  function openProductModal(cardInnerEl) {
    // Shop gesloten vandaag → blokkeer bestel-flow, toon vriendelijke modal
    if (isShopClosed()) { openClosedModal(); return; }
    var jsonStr = cardInnerEl.dataset.productJson;
    if (!jsonStr) return;
    try {
      modalProductData = JSON.parse(jsonStr);
    } catch(e) {
      console.error('FrietchaletMenu: product JSON parse error', e);
      return;
    }
    modalQty = 1;
    modalSelectedVariantId = null;
    modalSelectedOptions = [];

    // Volledige beschrijving staat op de kaart zelf, niet in de JSON —
    // zo staat elke producttekst maar één keer in de HTML.
    var cardEl = cardInnerEl.closest ? cardInnerEl.closest('.menu-item') : cardInnerEl.parentElement;
    modalDescription = (cardEl && cardEl.dataset.productDescription) || '';

    // Als product geen variants heeft, selecteer eerste variant direct
    if (!modalProductData.has_variants && modalProductData.variants && modalProductData.variants.length > 0) {
      modalSelectedVariantId = modalProductData.variants[0].id;
    }

    renderProductModal();
    var overlay = document.getElementById('frietchalet-product-modal-overlay');
    if (overlay) overlay.classList.remove('hidden');
  }

  function closeProductModal() {
    var overlay = document.getElementById('frietchalet-product-modal-overlay');
    if (overlay) overlay.classList.add('hidden');
    modalProductData = null;
  }

  function selectOption(optIdx, value) {
    modalSelectedOptions[optIdx] = value;
    resolveVariantFromOptions();
    renderProductModal();
  }

  // Zoekt de variant die op ELKE optiepositie overeenkomt met de keuze.
  // (Eerder werd de eerste variant genomen die de waarde ergens bevatte —
  //  bij twee opties leverde dat de verkeerde combinatie op.)
  function resolveVariantFromOptions() {
    var item = modalProductData;
    modalSelectedVariantId = null;
    if (!item || !item.variants) return;
    var nOpts = (item.options_with_values || []).length;
    for (var i = 0; i < nOpts; i++) {
      if (!modalSelectedOptions[i]) return;
    }
    for (var k = 0; k < item.variants.length; k++) {
      var v = item.variants[k], ok = true;
      for (var j = 0; j < nOpts; j++) {
        if (!v.options || v.options[j] !== modalSelectedOptions[j]) { ok = false; break; }
      }
      if (ok) { modalSelectedVariantId = v.id; return; }
    }
  }

  function optionValueLabel(val) {
    return (val && typeof val === 'object' && val.name) ? String(val.name) : String(val);
  }

  function optionValueReachable(optIdx, valStr) {
    var item = modalProductData;
    if (!item || !item.variants) return true;
    for (var k = 0; k < item.variants.length; k++) {
      var v = item.variants[k];
      if (!v.options || v.options[optIdx] !== valStr) continue;
      if (v.available === false) continue;
      var conflict = false;
      for (var j = 0; j < modalSelectedOptions.length; j++) {
        if (j !== optIdx && modalSelectedOptions[j] && v.options[j] !== modalSelectedOptions[j]) { conflict = true; break; }
      }
      if (!conflict) return true;
    }
    return false;
  }

  function changeModalQty(delta) {
    modalQty = Math.max(1, modalQty + delta);
    renderProductModal();
  }

  function addToCartFromModal() {
    if (!modalProductData || !modalSelectedVariantId) return;
    addToCart(modalSelectedVariantId, modalQty).then(function() {
      closeProductModal();
    });
  }

  function renderProductModal() {
    var container = document.getElementById('frietchalet-product-modal');
    if (!container || !modalProductData) return;

    var item = modalProductData;
    var hasVariants = item.has_variants;
    var canAdd = !hasVariants || modalSelectedVariantId !== null;

    // Prijs op basis van geselecteerde variant
    var currentPrice = item.price;
    if (modalSelectedVariantId && item.variants) {
      var selectedVar = null;
      for (var i = 0; i < item.variants.length; i++) {
        if (item.variants[i].id === modalSelectedVariantId) {
          selectedVar = item.variants[i];
          break;
        }
      }
      if (selectedVar) currentPrice = selectedVar.price;
    }
    var total = formatMoney(currentPrice * modalQty);

    // Opties HTML
    var optionsHTML = '';
    if (hasVariants && item.options_with_values) {
      optionsHTML = item.options_with_values.map(function(option, optIdx) {
        var valuesHTML = option.values.map(function(val) {
          var valStr = optionValueLabel(val);
          var isSelected = modalSelectedOptions[optIdx] === valStr;
          var reachable = optionValueReachable(optIdx, valStr);
          var handler = reachable
            ? ' onclick="FrietchaletMenu.selectOption(' + optIdx + ', this.dataset.value)"' +
              ' onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();FrietchaletMenu.selectOption(' + optIdx + ', this.dataset.value);}"' +
              ' tabindex="0"'
            : '';
          return '<div class="option-row' + (reachable ? '' : ' unavailable') + '"' +
              ' role="radio" aria-checked="' + (isSelected ? 'true' : 'false') + '"' +
              (reachable ? '' : ' aria-disabled="true"') +
              ' data-value="' + escapeHtml(valStr) + '"' + handler + '>' +
            '<span class="option-name">' + escapeHtml(valStr) + (reachable ? '' : ' <em>(uitverkocht)</em>') + '</span>' +
            '<div class="option-radio' + (isSelected ? ' selected' : '') + '"></div>' +
          '</div>';
        }).join('');

        return '<div class="product-modal-options" role="radiogroup" aria-label="' + escapeHtml(String(option.name)) + '">' +
          '<div class="options-label-row">' +
            '<span class="options-label">' + escapeHtml(String(option.name)) + '</span>' +
            '<span class="options-required-badge">Verplicht</span>' +
          '</div>' +
          valuesHTML +
        '</div>';
      }).join('');
    }

    var imgSrc = item.image_large || item.image || '';
    var imgHTML = imgSrc
      ? '<img src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(item.title) + '" />'
      : '';

    container.innerHTML =
      '<div class="product-modal-img-wrap' + (hasVariants ? ' compact' : '') + '">' +
        imgHTML +
        '<div class="product-modal-img-gradient"></div>' +
        '<button class="product-modal-close" onclick="FrietchaletMenu.closeProductModal()" aria-label="Sluiten">' +
          '<svg class="fi" style="font-size:16px;line-height:1" aria-hidden="true"><use href="#fi-rr-cross-small"/></svg>' +
        '</button>' +
        (hasVariants ? '<div class="product-modal-img-title">' + escapeHtml(item.title) + '</div>' : '') +
      '</div>' +

      '<div class="product-modal-body">' +
        (!hasVariants ? (
          '<div class="product-modal-info">' +
            '<h2 class="product-modal-title">' + escapeHtml(item.title) + '</h2>' +
            '<p class="product-modal-price">' + formatMoney(item.price) + '</p>' +
            (modalDescription ? '<p class="product-modal-desc">' + escapeHtml(modalDescription) + '</p>' : '') +
          '</div>'
        ) : (
          modalDescription
            ? '<div class="product-modal-info"><p class="product-modal-desc">' + escapeHtml(modalDescription) + '</p></div>'
            : ''
        )) +
        optionsHTML +
      '</div>' +

      '<div class="product-modal-footer">' +
        '<div class="modal-qty-controls">' +
          '<button class="modal-qty-btn" onclick="FrietchaletMenu.changeModalQty(-1)"' + (modalQty <= 1 ? ' disabled' : '') + ' aria-label="Minder">' +
            '<svg class="fi" style="font-size:14px;line-height:1" aria-hidden="true"><use href="#fi-rr-minus"/></svg>' +
          '</button>' +
          '<span class="modal-qty-value">' + modalQty + '</span>' +
          '<button class="modal-qty-btn" onclick="FrietchaletMenu.changeModalQty(1)" aria-label="Meer">' +
            '<svg class="fi" style="font-size:14px;line-height:1" aria-hidden="true"><use href="#fi-rr-plus"/></svg>' +
          '</button>' +
        '</div>' +
        '<button class="modal-add-btn" onclick="FrietchaletMenu.addToCartFromModal()"' + (!canAdd ? ' disabled' : '') + '>' +
          '<span>Toevoegen</span>' +
          '<span>' + total + '</span>' +
        '</button>' +
      '</div>';
  }

  // ─── INFO MODAL ──────────────────────────────────────────

  function openInfoModal(tab) {
    activeInfoTab = tab || 'reviews';
    var overlay = document.getElementById('frietchalet-info-modal-overlay');
    if (overlay) overlay.classList.remove('hidden');
    renderInfoTabContent();
    updateInfoTabs();
  }

  function closeInfoModal() {
    var overlay = document.getElementById('frietchalet-info-modal-overlay');
    if (overlay) overlay.classList.add('hidden');
  }

  function setInfoTab(tab) {
    activeInfoTab = tab;
    renderInfoTabContent();
    updateInfoTabs();
  }

  function updateInfoTabs() {
    ['reviews', 'info', 'offers'].forEach(function(t) {
      var btn = document.getElementById('frietchalet-info-tab-' + t);
      if (btn) btn.classList.toggle('active', t === activeInfoTab);
    });
  }

  function renderStars(rating) {
    var pct = (parseFloat(rating) / 5) * 100;
    return '<div class="star-rating">' +
      '<div class="stars-empty">\u2605\u2605\u2605\u2605\u2605</div>' +
      '<div class="stars-filled" style="width:' + pct + '%">\u2605\u2605\u2605\u2605\u2605</div>' +
    '</div>';
  }

  function renderInfoTabContent() {
    var body = document.getElementById('frietchalet-info-modal-body');
    if (!body) return;
    var d = infoData;

    if (activeInfoTab === 'reviews') {
      body.innerHTML =
        '<div class="info-tab-content">' +
          '<h3 class="info-section-title">Totaalscore</h3>' +
          '<div class="review-score-row">' +
            '<span class="review-score-number">' + escapeHtml(String(d.rating || '0')) + '</span>' +
            '<div class="review-score-right">' +
              renderStars(d.rating) +
              '<p class="review-count">uit ' + escapeHtml(String(d.review_count || '0')) + ' reviews</p>' +
            '</div>' +
          '</div>' +
          (d.review_disclaimer ? '<p class="review-disclaimer">' + escapeHtml(d.review_disclaimer) + '</p>' : '') +
        '</div>';

    } else if (activeInfoTab === 'info') {
      var hoursHTML = '';
      if (d.opening_hours) {
        var rows = d.opening_hours.split(',').map(function(line) {
          var parts = line.trim().split('|');
          return parts.length === 2
            ? '<tr><td>' + escapeHtml(parts[0].trim()) + '</td><td>' + escapeHtml(parts[1].trim()) + '</td></tr>'
            : '';
        }).join('');
        hoursHTML =
          '<div class="info-section">' +
            '<h3 class="info-section-title">Openingsuren</h3>' +
            '<table class="pickup-table">' + rows + '</table>' +
          '</div>';
      }

      body.innerHTML =
        (d.map_embed_url && d.map_embed_url.startsWith('https://') ?
          '<div class="info-map-wrap">' +
            '<iframe src="' + escapeHtml(d.map_embed_url) + '" class="info-map-iframe" title="Kaart" loading="lazy"></iframe>' +
            '<div class="info-map-card">' +
              '<p class="info-map-address">' + escapeHtml(d.address || '') + '</p>' +
              '<p class="info-map-city">' + escapeHtml(d.city || '') + '</p>' +
            '</div>' +
          '</div>'
        : '') +
        '<div class="info-tab-content">' +
          (d.about_text ?
            '<div class="info-section"><h3 class="info-section-title">Over ons</h3><p class="info-text">' + escapeHtml(d.about_text) + '</p></div>'
          : '') +
          hoursHTML +
          '<div class="info-section">' +
            '<h3 class="info-section-title">Bedrijfsgegevens</h3>' +
            '<p class="info-text">' + escapeHtml(d.merchant_name || '') + '<br>' + escapeHtml(d.address || '') + '<br>' + escapeHtml(d.city || '') + '</p>' +
            (d.merchant_email ? '<a href="mailto:' + escapeHtml(d.merchant_email) + '" class="info-link" style="display:block;margin-top:8px;">Stuur ons een e-mail</a>' : '') +
            (d.vat_number ? '<p class="info-text" style="margin-top:10px;">BTW-nummer: ' + escapeHtml(d.vat_number) + '</p>' : '') +
          '</div>' +
        '</div>';

    } else if (activeInfoTab === 'offers') {
      body.innerHTML =
        '<div class="info-tab-content">' +
          (d.promo_text ?
            '<div class="offer-card"><span class="offer-card-text">' + escapeHtml(d.promo_text) + '</span></div>'
          : '<p class="info-text">Geen aanbiedingen op dit moment.</p>') +
        '</div>';
    }
  }

  // ─── CATEGORY MODAL ──────────────────────────────────────

  function openCategoryModal() {
    var overlay = document.getElementById('frietchalet-cat-modal-overlay');
    if (overlay) overlay.classList.remove('hidden');
  }

  function closeCategoryModal() {
    var overlay = document.getElementById('frietchalet-cat-modal-overlay');
    if (overlay) overlay.classList.add('hidden');
  }

  function selectCategoryFromModal(handle) {
    closeCategoryModal();
    scrollToCategory(handle);
  }

  // ─── MOBILE CART ─────────────────────────────────────────

  function getDingdoongRoot() {
    // Dingdoong renders via Cart drawer setting "Before #frietchalet-cart-items"
    // as #ot-dingdoong-root-cart-drawer, NOT into #dingdoong-embedded-render-root
    return document.getElementById('ot-dingdoong-root-cart-drawer');
  }

  function openMobileCart() {
    mobileCartOpen = true;
    document.body.style.overflow = 'hidden';
    var overlay = document.getElementById('frietchalet-mobile-cart-overlay');
    if (!overlay) return;
    // Move Dingdoong widget to mobile wrap BEFORE revealing (avoids layout jump)
    var widget = getDingdoongRoot();
    var mobileWrap = overlay.querySelector('.mobile-dingdoong-wrap');
    if (widget && mobileWrap && widget.parentElement !== mobileWrap) {
      mobileWrap.appendChild(widget);
    }
    // Remove hidden, then force reflow, then add is-open to trigger CSS transition
    overlay.classList.remove('hidden');
    void overlay.offsetHeight; // reflow — makes transition fire after display change
    overlay.classList.add('is-open');
  }

  function closeMobileCart() {
    mobileCartOpen = false;
    document.body.style.overflow = '';
    var overlay = document.getElementById('frietchalet-mobile-cart-overlay');
    if (!overlay) return;
    // Remove is-open first — CSS transition animates out
    overlay.classList.remove('is-open');
    // Hide after transition completes (300ms matches CSS transition duration)
    setTimeout(function() {
      overlay.classList.add('hidden');
      // Move Dingdoong widget back before #frietchalet-cart-items (original position)
      var widget = getDingdoongRoot();
      var cartItems = document.getElementById('frietchalet-cart-items');
      if (widget && cartItems && widget.parentElement !== cartItems.parentElement) {
        cartItems.parentElement.insertBefore(widget, cartItems);
      }
    }, 300);
  }

  // ─── HELPERS ─────────────────────────────────────────────

  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function triggerCartBadgePop() {
    var badge = document.getElementById('frietchalet-float-count');
    if (!badge) return;
    badge.classList.remove('fm-pop');
    void badge.offsetWidth; // trigger reflow
    badge.classList.add('fm-pop');
    badge.addEventListener('animationend', function() {
      badge.classList.remove('fm-pop');
    }, { once: true });
  }

  // ─── DINGDOONG INTEGRATION ──────────────────────────────
  //
  // Fix 1: Dingdoong JS set inline styles (max-height, overflow)
  //         yang menyebabkan scroll. MutationObserver memaksa
  //         menghapus scroll setiap kali Dingdoong merender ulang.
  //
  // Fix 2: Auto-prefill tanggal — simulasikan klik hari pertama
  //         yang tersedia (voorkeur: vandaag) zodra widget gereed is.
  //         Reset bij tab-switch (Ophalen ↔ Lokale levering).
  //
  // Fix 3: Saat user klik "Afrekenen", baca nilai dari Dingdoong
  //         DOM, simpan ke cart attributes via /cart/update.js,
  //         lalu redirect ke /checkout.

  function initDingdoongFix() {
    // Dingdoong renders as #ot-dingdoong-root-cart-drawer (via Cart drawer setting).
    // It loads async, so we observe the cart-items parent to detect when it appears.
    var cartItems = document.getElementById('frietchalet-cart-items');
    if (!cartItems || !cartItems.parentElement) return;

    function removeDingdoongScroll() {
      var root = getDingdoongRoot();
      if (!root) return;
      root.style.overflow = 'visible';
      root.style.maxHeight = 'none';
      var main = root.querySelector('.ot-dingdoong-main');
      if (main) {
        main.style.overflow = 'visible';
        main.style.maxHeight = 'none';
      }
      var children = root.querySelectorAll('[style*="overflow"], [style*="max-height"]');
      for (var i = 0; i < children.length; i++) {
        // NIET de keuzelijst-popover aanraken. Dingdoong opent en sluit die door zélf
        // de inline max-height te animeren (0 → hoogte). Zetten wij hem op 'none',
        // dan kan de tijdlijst nooit meer dichtklappen — en omdat de MutationObserver
        // hieronder op style-wijzigingen luistert, forceerden we hem bij elke poging
        // tot sluiten opnieuw open (gemeten op Frietchalet: lijst bleef permanent
        // openstaan met 33 items, ook na het kiezen van een tijd).
        if (children[i].closest && children[i].closest('.ot-dingdoong-dropdown__content')) continue;
        children[i].style.overflow = 'visible';
        children[i].style.maxHeight = 'none';
      }
    }

    removeDingdoongScroll();

    // Observe parent of cart-items — Dingdoong inserts itself as sibling
    var observer = new MutationObserver(function() {
      removeDingdoongScroll();
    });
    observer.observe(cartItems.parentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  }

  // ─── DINGDOONG AUTO-PREFILL DATE ─────────────────────────
  //
  // Adapted from dingdoong-style-override pattern.
  // Verschil: cart-drawer mode gebruikt .ot-dingdoong-calendar__popover-button
  // als trigger (geen #dd-container dropdown). Kalender-popup rendert naar body.
  //
  // Fase-flow: waiting → opening → selecting → done
  // Reset bij Ophalen/Levering tab-switch.

  function initDingdoongPrefill() {
    var _prefillDone  = false;
    var _prefillPhase = 'waiting';

    function isPrefillNeeded() {
      if (_prefillDone) return false;
      var root = getDingdoongRoot();
      if (!root) return false;

      // Primair: verborgen tekst-element gevuld door Dingdoong na selectie
      var dateVal = root.querySelector('.ot-delivery-date_date-input-date-value');
      if (dateVal && dateVal.textContent.trim()) return false;

      // Secundair: kalender popover knop — lees label
      var calBtn = root.querySelector('.ot-dingdoong-calendar__popover-button');
      if (calBtn) {
        var txt = calBtn.textContent.trim().toLowerCase();
        // Placeholder-teksten geven aan dat nog niets geselecteerd is
        if (txt === '' || txt.indexOf('datum') !== -1 || txt.indexOf('pick') !== -1 ||
            txt.indexOf('select') !== -1 || txt.indexOf('kies') !== -1) {
          return true;
        }
        return false; // Datum al gevuld
      }

      // Fallback: als er een dag al geselecteerd is in een zichtbare kalender
      var selected = document.querySelector('.ot-dingdoong-calendar__selected');
      return !selected;
    }

    function simulateClick(el) {
      ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach(function(type) {
        el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }));
      });
    }

    function getFirstAvailableDay() {
      // Kalender-popup rendert naar body, zoek globaal
      var today = document.querySelector(
        '.ot-dingdoong-calendar__today:not(.ot-dingdoong-calendar__disabled) .ot-dingdoong-calendar__day-button:not([disabled])'
      );
      var first = document.querySelector(
        '.ot-dingdoong-calendar__day:not(.ot-dingdoong-calendar__disabled):not(.ot-dingdoong-calendar__hidden):not(.ot-dingdoong-calendar__outside) .ot-dingdoong-calendar__day-button:not([disabled])'
      );
      return today || first;
    }

    function runPrefill() {
      if (_prefillDone) return;
      if (!isPrefillNeeded()) {
        _prefillDone = true;
        _prefillPhase = 'done';
        return;
      }

      var root = getDingdoongRoot();
      if (!root) return;

      if (_prefillPhase === 'waiting') {
        // Check of kalender al inline zichtbaar is (geen popover nodig)
        if (getFirstAvailableDay()) {
          _prefillPhase = 'selecting';
          runPrefill();
          return;
        }
        // Open de kalender via popover-knop
        var calBtn = root.querySelector('.ot-dingdoong-calendar__popover-button');
        if (calBtn) {
          _prefillPhase = 'opening';
          simulateClick(calBtn);
          setTimeout(runPrefill, 500);
        }
        return;
      }

      if (_prefillPhase === 'opening') {
        if (getFirstAvailableDay()) {
          _prefillPhase = 'selecting';
          runPrefill();
        } else {
          setTimeout(runPrefill, 300); // Kalender nog niet zichtbaar, herprobeer
        }
        return;
      }

      if (_prefillPhase === 'selecting') {
        var target = getFirstAvailableDay();
        if (target) {
          simulateClick(target);
          setTimeout(function() {
            if (!isPrefillNeeded()) {
              _prefillDone = true;
              _prefillPhase = 'done';
            } else {
              // Klik niet geregistreerd, opnieuw proberen
              _prefillPhase = 'waiting';
              setTimeout(runPrefill, 500);
            }
          }, 400);
        }
        return;
      }
    }

    // Poll totdat Dingdoong widget geladen is (max 15 sec)
    var _attempts = 0;
    var _poll = setInterval(function() {
      _attempts++;
      if (_prefillDone || _attempts > 30) { clearInterval(_poll); return; }
      if (getDingdoongRoot() && _prefillPhase === 'waiting') {
        setTimeout(runPrefill, 800);
      }
    }, 500);

    // MutationObserver: vang het moment dat de widget in de DOM verschijnt
    var _rootObs = new MutationObserver(function() {
      if (_prefillDone) { _rootObs.disconnect(); return; }
      if (getDingdoongRoot() && _prefillPhase === 'waiting') {
        setTimeout(runPrefill, 600);
      }
    });
    _rootObs.observe(document.body, { childList: true, subtree: false });

    // Reset bij Ophalen ↔ Lokale levering tab-switch
    document.addEventListener('click', function(e) {
      if (e.target.closest && e.target.closest('.ot-dingdoong-header__radio-label')) {
        _prefillDone  = false;
        _prefillPhase = 'waiting';
        setTimeout(runPrefill, 800);
        setTimeout(runPrefill, 1500);
      }
    }, true);
  }

  function getDingdoongValues() {
    var values = {};
    var root = getDingdoongRoot();
    if (!root) return values;

    // Fulfillment method (Ophalen / Lokale levering)
    var checkedRadio = root.querySelector('.ot-dingdoong-header__radio-label.checked .ot-dingdoong-header__radio-label-text');
    if (checkedRadio) {
      values['Delivery Method'] = checkedRadio.textContent.trim();
    }

    // Datum. Twee bronnen: de leesbare knoptekst en de verborgen ISO-waarde.
    // Alleen tekst mét een cijfer telt — anders glipt een placeholder als
    // 'Lokale leverdatum' als datum de bestelling in (dat is echt gebeurd).
    var datumLeesbaar = '';
    var dateBtn = root.querySelector('.ot-dingdoong-calendar__popover-button');
    if (dateBtn && heeftCijfer(dateBtn.textContent)) {
      datumLeesbaar = dateBtn.textContent.trim();
    }
    /* De verborgen ISO-waarde van DingDoong is niet te vertrouwen: hij liep in de
       praktijk een dag achter op de knop (7 aug 15:00Z terwijl de klant 8 augustus
       zag). Daarom rekenen we de machineleesbare datum zelf uit de zichtbare tekst
       — dat is precies wat de klant gekozen heeft. Alleen als die tekst ontbreekt
       vallen we terug op de verborgen waarde. */
    var datumDatum = nederlandsNaarDatum(datumLeesbaar);
    if (!datumLeesbaar) {
      var isoEl = root.querySelector('.ot-delivery-date_date-input-date-value');
      var isoTxt = isoEl ? isoEl.textContent.trim() : '';
      if (/^\d{4}-\d{2}-\d{2}T/.test(isoTxt)) {
        datumLeesbaar = isoNaarNederlands(isoTxt);
        datumDatum = isoTxt.slice(0, 10);
      }
    }

    /* "Zo snel mogelijk" (DingDoong ASAP): dan toont DingDoong geen kalender en
       staat de vroegste dag in het onderschrift van die optie. De tijd (eerste
       vrije slot na de bereidingstijd) zit gewoon in #ot-time-slot. */
    if (!datumLeesbaar) {
      var asapCap = root.querySelector('.ot-dingdoong-fulfillment-speed__option.checked .ot-dingdoong-fulfillment-speed__caption');
      var asapTxt = asapCap && asapCap.firstChild ? asapCap.firstChild.textContent.trim() : '';
      if (heeftCijfer(asapTxt)) {
        datumLeesbaar = asapTxt;
        datumDatum = nederlandsNaarDatum(asapTxt);
      }
    }

    if (datumLeesbaar) values['Delivery Date'] = datumLeesbaar;
    if (datumDatum) values['Delivery Date ISO'] = datumDatum;

    // Tijd — verborgen input #ot-time-slot is de betrouwbare bron.
    // De zichtbare label wordt alleen gebruikt als hij een echte klok bevat.
    var timeInput = root.querySelector('#ot-time-slot');
    var tijd = '';
    if (timeInput && timeInput.value) {
      try {
        var slot = JSON.parse(timeInput.value);
        if (slot.start) {
          tijd = slot.start + (slot.end && slot.end !== slot.start ? ' - ' + slot.end : '');
        }
      } catch (e) {
        tijd = timeInput.value;
      }
    }
    if (!heeftKlok(tijd)) {
      var timeLabel = root.querySelector('.ot-dingdoong-dropdown__label .dd-label');
      var labelTxt = timeLabel ? timeLabel.textContent.trim() : '';
      tijd = heeftKlok(labelTxt) ? labelTxt : '';
    }
    if (tijd) values['Delivery Time'] = tijd;

    return values;
  }

  // ─── SLOT-CONTROLE: geen datum + tijd, geen checkout ─────
  //
  // Waarom dit moet: het thema vult de datum automatisch in, dus 'er is een
  // waarde' betekende altijd waar en de knop liet iedereen door. Zonder tijd
  // verzint de kassa-koppeling er zelf een (nu + 15 min) en print de keuken een
  // moment dat de klant nooit gekozen heeft.
  var MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
                 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  var DAGEN = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];

  function heeftCijfer(txt) { return !!txt && /\d/.test(txt); }
  function heeftKlok(txt) { return !!txt && /\d{1,2}[:.]\d{2}/.test(txt); }

  /* 'zaterdag, 08 augustus 2026' → '2026-08-08' */
  function nederlandsNaarDatum(txt) {
    var m = /(\d{1,2})\s+([a-z]+)\s+(\d{4})/i.exec(txt || '');
    if (!m) return '';
    var maand = MAANDEN.indexOf(m[2].toLowerCase());
    if (maand < 0) return '';
    return m[3] + '-' + ('0' + (maand + 1)).slice(-2) + '-' + ('0' + m[1]).slice(-2);
  }

  function isoNaarNederlands(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return DAGEN[d.getDay()] + ', ' + ('0' + d.getDate()).slice(-2) + ' ' +
           MAANDEN[d.getMonth()] + ' ' + d.getFullYear();
  }

  function slotOntbreekt() {
    var v = getDingdoongValues();
    if (!v['Delivery Date'] && !v['Delivery Time']) return 'beide';
    if (!v['Delivery Time']) return 'tijd';
    if (!v['Delivery Date']) return 'datum';
    return '';
  }

  function checkoutKnoppen() {
    return [
      document.getElementById('frietchalet-checkout-btn'),
      document.getElementById('frietchalet-mobile-checkout-btn')
    ].filter(Boolean);
  }

  function slotHintTekst(wat) {
    if (wat === 'datum') return 'Kies eerst een datum';
    if (wat === 'beide') return 'Kies eerst een datum en een tijd';
    return 'Kies eerst een tijd';
  }

  /* Hint staat onder de knop, niet erin: de knoptekst bevat het totaalbedrag
     en dat wil je niet kwijtraken. */
  function zetHint(btn, tekst) {
    var wrap = btn.parentElement;
    if (!wrap) return;
    var hint = wrap.querySelector('.checkout-slot-hint');
    if (!tekst) { if (hint) hint.remove(); return; }
    if (!hint) {
      hint = document.createElement('button');
      hint.type = 'button';
      hint.className = 'checkout-slot-hint';
      hint.addEventListener('click', function () { wijsNaarSlot(); });
      wrap.appendChild(hint);
    }
    if (hint.textContent !== tekst) hint.textContent = tekst;
  }

  function bewaakCheckoutKnop() {
    var wat = slotOntbreekt();
    checkoutKnoppen().forEach(function (btn) {
      /* aria-disabled i.p.v. disabled: een echt uitgeschakelde knop stuurt geen
         klik meer, en dan kunnen we het veld ook niet meer aanwijzen. */
      if (wat) {
        btn.classList.add('is-slot-blocked');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.classList.remove('is-slot-blocked');
        btn.removeAttribute('aria-disabled');
      }
      zetHint(btn, wat ? slotHintTekst(wat) : '');
    });
  }

  function wijsNaarSlot() {
    var root = getDingdoongRoot();
    if (!root) return;
    var wat = slotOntbreekt();
    var doel = (wat === 'datum')
      ? root.querySelector('.ot-dingdoong-calendar__popover-button')
      : root.querySelector('.ot-dingdoong-time-picker-container');
    var el = doel || root;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    el.classList.add('slot-attentie');
    setTimeout(function () { el.classList.remove('slot-attentie'); }, 2600);
  }

  function initSlotBewaking() {
    if (!checkoutKnoppen().length) return;
    bewaakCheckoutKnop();
    /* DingDoong schrijft de tijd als property op een verborgen input; daar vuurt
       geen event en geen MutationObserver op. Een lichte poll is hier eerlijker
       dan een brede observer (die maakte de menupagina eerder traag). */
    setInterval(bewaakCheckoutKnop, 1000);
  }

  function handleCheckout(e) {
    if (e) e.preventDefault();
    // Winkel vandaag gesloten → niet afrekenen, wel uitleggen waarom
    if (isShopClosed()) { openClosedModal(); return; }
    // Zonder gekozen datum én tijd niet doorlaten — de keuken moet weten wanneer.
    if (slotOntbreekt()) { bewaakCheckoutKnop(); wijsNaarSlot(); return; }
    // Disable both desktop and mobile checkout buttons
    var btns = [
      document.getElementById('frietchalet-checkout-btn'),
      document.getElementById('frietchalet-mobile-checkout-btn')
    ];
    btns.forEach(function(btn) {
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Even geduld...';
      }
    });

    // Safety net: re-enable als navigatie niet lukt (bijv. browser extension blokt redirect)
    var resetTimer = setTimeout(function() {
      btns.forEach(function(btn) {
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Afrekenen';
        }
      });
    }, 8000);

    var dingdoongValues = getDingdoongValues();
    var attributes = {};
    var hasValues = false;

    for (var key in dingdoongValues) {
      if (dingdoongValues[key]) {
        attributes[key] = dingdoongValues[key];
        hasValues = true;
      }
    }

    if (!hasValues) {
      window.location.href = '/checkout';
      return;
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    })
    .then(function() {
      window.location.href = '/checkout';
    })
    .catch(function(err) {
      console.error('FrietchaletMenu: cart attributes save error', err);
      window.location.href = '/checkout';
    });
  }

  // ─── INIT ON DOM READY ───────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      init();
      initDingdoongFix();
      initDingdoongPrefill();
      initSlotBewaking();
    });
  } else {
    init();
    initDingdoongFix();
    initDingdoongPrefill();
    initSlotBewaking();
  }

  // ─── CLOSED-TODAY MODAL ──────────────────────────────────
  /* Twee onafhankelijke bronnen, zodat een falende config de blokkade niet opheft. */
  function isShopClosed() {
    return !!(config.is_closed_today || window.FrietchaletClosed);
  }
  /* De melding zelf staat themabreed in layout/theme.liquid. */
  function openClosedModal() {
    if (typeof window.FrietchaletShowClosed === 'function') window.FrietchaletShowClosed();
  }
  function closeClosedModal() {
    if (typeof window.FrietchaletHideClosed === 'function') window.FrietchaletHideClosed();
  }

  // ─── PUBLIC API ──────────────────────────────────────────


  // ─── DEEPLINK VANAF DE HOMEPAGE ──────────────────────────
  // Een CTA elders op de site linkt naar deze pagina met ?product=<id of handle>
  // (optioneel &add=1). Doel: de klant landt op het menu met het juiste product
  // open, in plaats van op een productpagina buiten de bestelflow.
  //
  // Bewust GEEN stille toevoeging aan de winkelwagen bij producten met opties:
  // dan zou een standaardvariant gekozen worden die de klant nooit zag. Bij
  // varianten openen we de modal zodat de klant zelf kiest; alleen producten met
  // één variant gaan direct in de winkelwagen wanneer &add=1 meegegeven is.
  function handleDeepLink() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var wanted = params.get('product');
    if (!wanted) return;
    wanted = String(wanted).trim().toLowerCase();
    var wantAdd = params.get('add') === '1';

    var cards = document.querySelectorAll('.menu-item');
    var target = null;
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var id = (c.dataset.productId || '').toLowerCase();
      var handle = (c.dataset.productHandle || '').toLowerCase();
      if (id === wanted || handle === wanted) { target = c; break; }
    }
    if (!target) return;   // product niet in dit menu (bv. uitverkocht) → gewoon het menu tonen

    var section = target.closest ? target.closest('.menu-category-section') : null;
    var catHandle = section && section.id ? section.id.replace(/^section-/, '') : '';

    function reveal() {
      if (catHandle) scrollToCategory(catHandle);
      else target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('deeplink-highlight');
      setTimeout(function() { target.classList.remove('deeplink-highlight'); }, 2600);
    }

    // even wachten tot tabs/scrollspy klaar zijn, anders springt de pagina terug
    setTimeout(function() {
      reveal();
      if (!wantAdd) return;
      var inner = target.querySelector('[data-product-json]') || target;
      var data = null;
      try { data = JSON.parse(inner.dataset.productJson || 'null'); } catch (e) { data = null; }
      if (data && !data.has_variants && data.variants && data.variants.length === 1) {
        addToCart(data.variants[0].id, 1);
      } else {
        openProductModal(inner);   // opties → klant kiest zelf
      }
    }, 350);
  }

  return {
    handleDeepLink: handleDeepLink,
    // Cart
    addToCart: addToCart,
    changeCartItem: changeCartItem,
    fetchCart: fetchCart,
    // Tabs & Scroll
    scrollToCategory: scrollToCategory,
    scrollTabs: scrollTabs,
    // Search
    onMenuSearch: onMenuSearch,
    clearMenuSearch: clearMenuSearch,
    // Closed-today modal
    openClosedModal: openClosedModal,
    closeClosedModal: closeClosedModal,
    // Product Modal
    openProductModal: openProductModal,
    closeProductModal: closeProductModal,
    selectOption: selectOption,
    changeModalQty: changeModalQty,
    addToCartFromModal: addToCartFromModal,
    // Info Modal
    openInfoModal: openInfoModal,
    closeInfoModal: closeInfoModal,
    setInfoTab: setInfoTab,
    // Category Modal
    openCategoryModal: openCategoryModal,
    closeCategoryModal: closeCategoryModal,
    selectCategoryFromModal: selectCategoryFromModal,
    // Mobile Cart
    openMobileCart: openMobileCart,
    closeMobileCart: closeMobileCart,
    // Undo toast
    undoLastDelete: undoLastDelete,
    // Cart Item Note
    openCartItemNote: openCartItemNote,
    closeCartItemNote: closeCartItemNote,
    saveCartItemNote: saveCartItemNote,
    // Checkout (met Dingdoong data)
    handleCheckout: handleCheckout
  };
})();

/* ─── DINGDOONG TIJDLIJST — DICHTKLAPPEN NA KEUZE (workaround app-bug FC) ───
 *
 * Bewezen met een gecontroleerd experiment (theme-JS VOLLEDIG uitgeschakeld):
 * Dingdoong opent de tijdlijst nadat je een datum kiest, maar sluit hem daarna
 * NOOIT meer — niet na het kiezen van een tijd, niet bij een klik ernaast en ook
 * niet via zijn eigen knop. Dat gebeurt ook zonder één regel code van ons, dus het
 * is een bug in de app zelf (alleen Frietchalet; TZ/FB hebben er geen last van).
 *
 * Omdat de app intern "open" blijft staan, klappen wij de lijst dicht met een
 * inline max-height (gemeten: niets overschrijft die waarde). Klikt de klant daarna
 * ergens in het tijdblok — bijvoorbeeld op de knop om een andere tijd te kiezen —
 * dan halen we onze inline waarde weg en staat de lijst meteen weer open.
 * Verwijder dit blok zodra Omega de app-bug oplost.
 */
(function () {
  'use strict';
  if (window.__ddTimeClose) return; window.__ddTimeClose = true;

  var TIME = '.ot-dingdoong-time-picker-container';
  function box() { return document.querySelector(TIME + ' .ot-dingdoong-dropdown__content'); }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest(TIME)) return;

    if (t.closest('.ot-dingdoong-dropdown__content-item')) {
      // Tijd gekozen → dichtklappen (even wachten zodat de app de keuze verwerkt).
      setTimeout(function () { var el = box(); if (el) el.style.setProperty('max-height', '0px'); }, 200);
    } else {
      // Elders in het tijdblok geklikt (knop/label) → onze dichtklap opheffen.
      var el = box(); if (el) el.style.removeProperty('max-height');
    }
  }, true);

  // Bij het laden klapt Dingdoong de lijst open zodra er een datum staat. Omdat de
  // knop nu weer zichtbaar is, sluiten we die beginstand eenmalig — net als in de
  // andere winkels start de klant met een net keuzeveld.
  var tries = 0;
  var iv = setInterval(function () {
    tries++;
    var el = box();
    if (el && el.getBoundingClientRect().height > 8) {
      el.style.setProperty('max-height', '0px');
      clearInterval(iv);
    } else if (tries > 25) {
      clearInterval(iv);
    }
  }, 600);
})();
